import { PreliminaryPrice, SnakeMeta } from '@api';
import {
  checkSupportedExchange,
  formatPrice,
  getDisplayPriceChange,
  getMarketHours,
} from '@helpers';
import { LivePriceInfo } from '@terminal/hooks/use-live-price';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import { format } from 'date-fns';
import { TFunction } from 'i18next';

import { PriceBase, PriceDisplayReturn } from './price-display.types';

interface MapPriceDisplayProps {
  exchange: string;
  livePriceInfo?: LivePriceInfo;
  preliminaryPrice?: PreliminaryPrice;
  meta?: SnakeMeta;
  isHovered: boolean;
  t: TFunction;
}

/**
 * Main formatting function that returns price details for v2 price display
 *
 * Details: https://app.clickup.com/10607188/v/dc/a3pjm-10088/a3pjm-15448
 * @returns primary price and secondary price details
 */
export const formatPriceDisplay = ({
  exchange,
  livePriceInfo,
  preliminaryPrice,
  meta,
  isHovered,
  t,
}: MapPriceDisplayProps): PriceDisplayReturn => {
  const isSupported = checkSupportedExchange(exchange);
  const marketHours = getMarketHours();

  const lastKnownPrice =
    livePriceInfo?.lastKnownPrice !== undefined
      ? livePriceInfo.lastKnownPrice
      : meta?.last_value;
  const price =
    livePriceInfo?.price !== undefined ? livePriceInfo.price : lastKnownPrice;
  const decimals = livePriceInfo?.decimals ?? 2;

  const base: PriceBase = {
    price,
    priceChange: livePriceInfo?.priceChange,
    isLive: livePriceInfo?.isLive,
    marketHours,
    isSupported,
    asOfDate:
      livePriceInfo?.time && isHovered
        ? format(Date.parse(livePriceInfo?.time), 'PPP')
        : undefined,
  };

  const { primaryPrice, secondaryPrice } = formatPrice({
    base,
    meta,
    preliminaryPrice,
    decimals,
    dateLocale: useLanguageStore.getState().languageSetting,
    preMarketLabel: t('analyze:analysis.preMarket'),
    afterHoursLabel: t('analyze:analysis.afterHours'),
  });

  const filterInvalidChangeValues = <T extends PriceBase>(priceBase: T): T => ({
    ...priceBase,
    priceChange: getDisplayPriceChange(priceBase.priceChange, priceBase.isLive),
  });

  const primary = isSupported && !isHovered ? primaryPrice : base;
  const secondary = isSupported ? secondaryPrice : undefined;

  return {
    primary,
    secondary: secondary ? filterInvalidChangeValues(secondary) : undefined,
  };
};

export { getMarketHours };

export type PriceStampType = 'live' | 'notLive' | 'notSupported';

type TypeStampPrice = {
  color: string;
  stampType: PriceStampType;
};

interface GetPriceStampKeyProps {
  isLivePriceSupported: boolean;
  isLive: boolean;
  isDisabled: boolean;
}

export const getPriceStamp = ({
  isLivePriceSupported,
  isLive,
  isDisabled,
}: GetPriceStampKeyProps): TypeStampPrice => {
  const getStampColor = (color: string) =>
    isDisabled ? 'var(--icon-disabled)' : color;

  if (isLivePriceSupported) {
    if (!isLive) {
      return {
        color: getStampColor('var(--icon-disabled)'),
        stampType: 'notLive',
      };
    } else {
      return {
        color: getStampColor('var(--icon-success)'),
        stampType: 'live',
      };
    }
  }

  return {
    color: getStampColor('var(--icon-disabled)'),
    stampType: 'notSupported',
  };
};
